/**
 * Landing Page V2 - About Us Section
 *
 * Statistics, image, and about content section
 * 100% Design System Integration - NO hardcoded values
 */

'use client';

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import Image from 'next/image';
import Link from 'next/link';
import { useExperienceStats } from '@/hooks/useExperiences';
import { ScrollReveal } from './ScrollReveal';

interface StatCardProps {
  iconSrc: string;
  iconAlt: string;
  value: string;
  label: string;
  href?: string;
}

function StatCard({ iconSrc, iconAlt, value, label, href }: StatCardProps) {
  const cardContent = (
    <div
      style={{
        backgroundColor: backgroundColors.primary,
        borderColor: '#f0f0f0',
        borderWidth: borders.width.thin,
        borderStyle: 'solid',
        display: 'flex',
        gap: spacing[5], // 20px gap per Figma
        alignItems: 'center',
        paddingLeft: spacing[2], // 8px per Figma
        paddingRight: spacing[3], // 12px per Figma
        paddingTop: spacing[2], // 8px per Figma
        paddingBottom: spacing[2], // 8px per Figma
        borderRadius: '8px', // rounded-sm per Figma
        width: '100%',
        minWidth: 0, // Prevent flex items from overflowing
        cursor: href ? 'pointer' : 'default',
        transition: href ? 'opacity 0.2s ease' : 'none',
      }}
      className={href ? 'hover:opacity-80' : ''}
    >
      {/* Icon */}
      <div
        style={{
          backgroundColor: '#fbf8ea',
          borderColor: 'rgba(0, 0, 0, 0.12)',
          borderWidth: borders.width.thin,
          borderStyle: 'solid',
          display: 'flex',
          gap: spacing[2],
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing[2],
          borderRadius: '6px', // rounded-md per Figma
          width: '56px',
          height: '56px',
          flexShrink: 0,
        }}
      >
        <Image
          src={iconSrc}
          alt={iconAlt}
          width={40}
          height={40}
          style={{
            height: '40px',
            width: 'auto',
            maxHeight: '40px',
          }}
          className='object-contain'
        />
      </div>

      {/* Content */}
      <div
        style={{
          display: 'flex',
          flex: '1 0 0',
          flexDirection: 'column',
          gap: spacing[3], // 12px gap between value and label per Figma
          alignItems: 'flex-start',
          justifyContent: 'center',
          lineHeight: 'normal',
          minWidth: 0, // Prevent text overflow
        }}
      >
        <p
          style={{
            fontFamily: fontPrimary,
            fontWeight: typography.weight.semibold,
            fontSize: '32px', // 32px per Figma
            color: typographyColors.primary,
            margin: 0,
          }}
        >
          {value}
        </p>
        <p
          style={{
            fontFamily: fontPrimary,
            fontWeight: typography.weight.regular,
            fontSize: typography.size.base, // 16px per Figma
            color: '#767676', // Exact color per Figma
            margin: 0,
          }}
        >
          {label}
        </p>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} style={{ width: '100%', display: 'block' }}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

// Mobile-specific stat card with vertical layout (icon on top, content below)
function MobileStatCard({
  iconSrc,
  iconAlt,
  value,
  label,
  href,
}: StatCardProps) {
  const cardContent = (
    <div
      style={{
        backgroundColor: backgroundColors.primary,
        borderColor: '#f0f0f0',
        borderWidth: borders.width.thin,
        borderStyle: 'solid',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[5], // 20px gap per Figma
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: spacing[2], // 8px per Figma
        paddingRight: spacing[3], // 12px per Figma
        paddingTop: spacing[2], // 8px per Figma
        paddingBottom: spacing[2], // 8px per Figma
        borderRadius: '8px', // rounded-sm per Figma
        width: '100%',
        cursor: href ? 'pointer' : 'default',
        transition: href ? 'opacity 0.2s ease' : 'none',
      }}
      className={href ? 'hover:opacity-80' : ''}
    >
      {/* Icon */}
      <div
        style={{
          backgroundColor: '#fbf8ea',
          borderColor: 'rgba(0, 0, 0, 0.12)',
          borderWidth: borders.width.thin,
          borderStyle: 'solid',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing[2],
          borderRadius: '6px', // rounded-md per Figma
          width: '58px', // 40px icon + 16px padding (8px each side) + 2px border (1px each side)
          height: '58px', // 40px icon + 16px padding (8px each side) + 2px border (1px each side)
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Image
            src={iconSrc}
            alt={iconAlt}
            width={40}
            height={40}
            className='object-contain'
            style={{
              width: '40px',
              height: '40px',
              maxWidth: '40px',
              maxHeight: '40px',
              minWidth: '40px',
              minHeight: '40px',
              display: 'block',
            }}
          />
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3], // 12px gap between value and label per Figma
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 'normal',
          textAlign: 'center',
          width: '100%',
        }}
      >
        <p
          style={{
            fontFamily: fontPrimary,
            fontWeight: typography.weight.semibold,
            fontSize: typography.size['3xl'], // 30px per Figma mobile design
            color: typographyColors.primary,
            margin: 0,
          }}
        >
          {value}
        </p>
        <p
          style={{
            fontFamily: fontPrimary,
            fontWeight: typography.weight.regular,
            fontSize: typography.size.sm, // 14px per Figma mobile design
            color: '#767676', // Exact color per Figma
            margin: 0,
          }}
        >
          {label}
        </p>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} style={{ width: '100%', display: 'block' }}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

interface FeatureCardProps {
  iconSrc: string;
  iconAlt: string;
  text: string;
  iconHeight?: number;
}

function FeatureCard({
  iconSrc,
  iconAlt,
  text,
  iconHeight = 40,
}: FeatureCardProps) {
  return (
    <div
      className='flex flex-col md:flex-row items-center justify-center pl-4 md:pl-0'
      style={{
        backgroundColor: backgroundColors.primary,
        flex: '1 0 0', // Equal width for both cards per Figma
        gap: spacing[4],
        paddingRight: spacing[4], // Right padding 16px per Figma
        paddingTop: spacing[4], // Vertical padding 16px per Figma
        paddingBottom: spacing[4],
        borderRadius: '8px', // rounded-lg per Figma
        // No border per Figma design
        boxShadow: '0px 7px 18px rgba(41, 37, 22, 0.08)',
      }}
    >
      <div
        style={{
          backgroundColor: '#fef2e2', // Light beige background per Figma
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          flexShrink: 0,
        }}
      >
        <Image
          src={iconSrc}
          alt={iconAlt}
          width={iconHeight}
          height={iconHeight}
          className='object-contain'
          style={{ height: `${iconHeight}px`, width: 'auto' }}
        />
      </div>
      <p
        className='text-center md:text-left'
        style={{
          fontFamily: fontPrimary,
          fontWeight: typography.weight.semibold,
          fontSize: typography.size.sm,
          color: typographyColors.primary,
          lineHeight: '20px',
          margin: 0,
          flex: '1 0 0',
        }}
      >
        {text}
      </p>
    </div>
  );
}

export function AboutUsSection() {
  // Fetch experience stats from GET /nexgo-gateway/experiences/v1/stats
  const { data: stats, isLoading } = useExperienceStats();

  return (
    <ScrollReveal
      as='section'
      className='px-4 py-8 md:px-20 md:py-14 flex flex-col md:flex-row gap-8 md:gap-8 items-center relative max-w-[1400px] mx-auto'
      style={{
        paddingLeft: spacing[4], // 16px on mobile per Figma
        paddingRight: spacing[4], // 16px on mobile per Figma
        paddingTop: spacing[4], // 16px on mobile (reduced from 32px)
      }}
      direction='up'
      amount={0.25}
      margin='0px 0px -120px 0px'
    >
      {/* Statistics Column */}
      <ScrollReveal
        className='hidden md:flex flex-col items-start'
        style={{
          width: '292px', // Fixed width per Figma
          paddingTop: spacing[16],
          gap: spacing[4], // 16px gap between cards per Figma
        }}
        delay={0.1}
        direction='left'
      >
        {isLoading ? (
          // Loading skeleton for stats
          <>
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className='animate-pulse w-full rounded-lg'
                style={{
                  height: '72px',
                  backgroundColor: backgroundColors.secondary,
                }}
              />
            ))}
          </>
        ) : (
          <>
            <StatCard
              iconSrc='/assets/images/icons/curated_packages.svg'
              iconAlt='Nexgo Journeys icon'
              value={(
                (stats?.signatureJourneys ?? 0) + (stats?.curatedPackages ?? 0)
              ).toString()}
              label='Nexgo Journeys'
              href='/experiences?type=nexgo-journeys'
            />

            <StatCard
              iconSrc='/assets/images/icons/home.svg'
              iconAlt='Nexgo Spaces icon'
              value={stats?.boutiqueStays?.toString() ?? '0'}
              label='Nexgo Spaces'
              href='/stay-experience'
            />

            <StatCard
              iconSrc='/assets/images/icons/guided.svg'
              iconAlt='Guided activities icon'
              value={stats?.guidedActivities?.toString() ?? '0'}
              label='Guided Activities'
              href='/activities'
            />

            <StatCard
              iconSrc='/assets/images/icons/motorcycle.svg'
              iconAlt='Riders guides icon'
              value={stats?.ridersGuides?.toString() ?? '0'}
              label='Riders Guides'
              href='/riders-list'
            />
          </>
        )}
      </ScrollReveal>

      {/* Center Image with Ellipse Background - Desktop Only */}
      <ScrollReveal
        className='hidden md:block relative w-full md:w-[404px]'
        style={{
          paddingTop: spacing[8], // 32px on mobile per Figma
          height: '366px', // Fixed height on mobile per Figma
        }}
        delay={0.2}
        direction='none'
      >
        {/* Ellipse Background */}
        <div
          className='hidden md:block absolute z-0'
          style={{
            width: '309px',
            height: '309px',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '309px',
            background:
              'linear-gradient(150deg, var(--Colors-brand, #FDE047) 11.75%, rgba(253, 224, 71, 0.10) 59.34%, rgba(253, 224, 71, 0.00) 94.23%)',
          }}
        />

        {/* Image */}
        <div
          className='relative w-full rounded-lg overflow-hidden z-10'
          style={{
            height: '366px', // Fixed height on mobile per Figma
          }}
        >
          <Image
            src='/assets/images/about-us-hero-1.png'
            alt='About Nexgo'
            fill
            style={{ objectFit: 'cover' }}
          />
        </div>
      </ScrollReveal>

      {/* Content Column */}
      <ScrollReveal
        className='flex flex-1 flex-col gap-7 md:h-[430px] items-start justify-center w-full md:w-[480px] pt-6 md:pt-12'
        delay={0.3}
        direction='right'
      >
        {/* Title */}
        <ScrollReveal
          className='flex flex-col gap-1.5 items-center md:items-start w-full text-center md:text-left'
          delay={0.35}
          direction='up'
        >
          <p
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.regular,
              fontSize: typography.size.xl,
              lineHeight: 'normal',
              color: typographyColors.primary,
              margin: 0,
            }}
          >
            About Us
          </p>
          <div
            className='w-full'
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.semibold,
              fontSize: 'clamp(24px, 5vw, 48px)', // Responsive font size: 24px on mobile, up to 48px on desktop
              lineHeight: 'normal',
              color: typographyColors.primary,
            }}
          >
            <p style={{ margin: 0 }}>Discover Your Journey</p>
          </div>
        </ScrollReveal>

        {/* Feature Cards */}
        <ScrollReveal
          className='flex flex-row items-start w-full'
          style={{
            gap: spacing[6], // 24px gap between cards per Figma
          }}
          delay={0.4}
          direction='up'
        >
          <ScrollReveal delay={0.45} direction='up' amount={0.15}>
            <FeatureCard
              iconSrc='/assets/images/icons/luggage.svg'
              iconAlt='Luggage icon'
              text='Explore all that you need in one place.'
              iconHeight={32}
            />
          </ScrollReveal>
          <ScrollReveal delay={0.5} direction='up' amount={0.15}>
            <FeatureCard
              iconSrc='/assets/images/icons/heroicons_chat-bubble-left.svg'
              iconAlt='Chat icon'
              text='Personalised planning support from travel experts.'
              iconHeight={32}
            />
          </ScrollReveal>
        </ScrollReveal>

        {/* Description */}
        <ScrollReveal delay={0.55} direction='up'>
          <p
            className='text-center md:text-left'
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.regular,
              fontSize: typography.size.lg,
              lineHeight: 'normal',
              color: typographyColors.primary,
              margin: 0,
            }}
          >
            At Nexgo, we believe every journey is personal. Our AI-powered
            companion helps you uncover places, stories, and experiences that
            reflects your passions and purpose.
          </p>
        </ScrollReveal>

        {/* Mobile Only Image - Without Ellipses */}
        <ScrollReveal
          className='md:hidden relative w-full'
          style={{
            height: '300px', // Mobile height
            marginTop: spacing[6], // Gap above image
            marginBottom: spacing[6], // Gap below image
          }}
          delay={0.55}
          direction='up'
        >
          <div
            className='relative w-full rounded-lg overflow-hidden'
            style={{
              height: '300px',
            }}
          >
            <Image
              src='/assets/images/about-us-hero-1.png'
              alt='About Nexgo'
              fill
              style={{ objectFit: 'cover' }}
            />
          </div>
        </ScrollReveal>

        {/* Mobile Statistics - Show below description on mobile */}
        <ScrollReveal
          className='md:hidden grid grid-cols-2 w-full'
          style={{
            gap: spacing[4], // 16px gap per Figma
          }}
          delay={0.6}
          direction='up'
        >
          {isLoading ? (
            // Loading skeleton for stats
            <>
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className='animate-pulse w-full rounded-lg'
                  style={{
                    height: '120px',
                    backgroundColor: backgroundColors.secondary,
                  }}
                />
              ))}
            </>
          ) : (
            <>
              <MobileStatCard
                iconSrc='/assets/images/icons/curated_packages.svg'
                iconAlt='Nexgo Journeys icon'
                value={(
                  (stats?.signatureJourneys ?? 0) + (stats?.curatedPackages ?? 0)
                ).toString()}
                label='Nexgo Journeys'
                href='/experiences?type=nexgo-journeys'
              />

              <MobileStatCard
                iconSrc='/assets/images/icons/home.svg'
                iconAlt='Nexgo Spaces icon'
                value={stats?.boutiqueStays?.toString() ?? '0'}
                label='Nexgo Spaces'
                href='/stay-experience'
              />

              <MobileStatCard
                iconSrc='/assets/images/icons/guided.svg'
                iconAlt='Guided activities icon'
                value={stats?.guidedActivities?.toString() ?? '0'}
                label='Guided Activities'
                href='/activities'
              />

              <MobileStatCard
                iconSrc='/assets/images/icons/motorcycle.svg'
                iconAlt='Riders guides icon'
                value={stats?.ridersGuides?.toString() ?? '0'}
                label='Riders Guides'
                href='/riders-list'
              />
            </>
          )}
        </ScrollReveal>
      </ScrollReveal>
    </ScrollReveal>
  );
}
